import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { FiMenu, FiX, FiChevronDown, FiChevronRight } from "react-icons/fi";
import logo from "../../assets/logo.png";

import { getCategories, getAllProducts } from "../../api/api";
import { useSiteSettings } from "../../context/SiteSettingsContext";


export default function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [products, setProducts] = useState({});
  const [productsOpen, setProductsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const settings = useSiteSettings();

  const basePortalUrl = (settings?.portalUrl || process.env.REACT_APP_PORTAL_URL || "").replace(/\/+$/, "");
  const PORTAL_URL = basePortalUrl + "/Login";

  useEffect(() => {
    const fetchData = async () => {
      try {
        const cats = await getCategories();
        setCategories(cats);
        if (cats.length > 0) setActiveCat(cats[0].categoryId);

        const allProds = await getAllProducts();
        // Group products by categoryId
        const grouped = allProds.reduce((acc, p) => {
          if (!acc[p.categoryId]) acc[p.categoryId] = [];
          acc[p.categoryId].push(p);
          return acc;
        }, {});
        setProducts(grouped);
      } catch (err) {
        console.error("Header Data Fetch Error:", err);
      }
    };
    fetchData();
  }, []);

  const goProducts = (catId, productSlug = null) => {
    const params = new URLSearchParams();
    if (catId) params.set("cat", catId);
    if (productSlug) params.set("sub", productSlug); // sub is the slug in our new API logic
    navigate(`/products?${params.toString()}`);
    setProductsOpen(false);
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setProductsOpen(false);
      }
    };

    if (productsOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [productsOpen]);

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-black/60 backdrop-blur border-b border-white/10 shadow-header">
      <div className="container flex items-center justify-between h-20 text-sm tracking-wide">
        <NavLink to="/" className="flex items-center">
          <img src={logo} alt="Logo" className="h-10 w-auto" />
        </NavLink>

        <nav className="hidden md:flex items-center gap-10">
          <NavLink
            to="/"
            className={({ isActive }) =>
              `relative font-medium transition-all duration-300 group ${isActive ? "text-white" : "text-white/80 hover:text-white"
              }`
            }
          >
            HOME
            <span className="absolute left-0 bottom-0 w-0 h-[2px] bg-white transition-all duration-300 group-hover:w-full"></span>
          </NavLink>

          <NavLink
            to="/about"
            className={({ isActive }) =>
              `relative font-medium transition-all duration-300 group ${isActive ? "text-white" : "text-white/80 hover:text-white"
              }`
            }
          >
            ABOUT
            <span className="absolute left-0 bottom-0 w-0 h-[2px] bg-white transition-all duration-300 group-hover:w-full"></span>
          </NavLink>

          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setProductsOpen((v) => !v)}
              className="flex items-center gap-1 text-white/80 hover:text-white font-medium"
            >
              PRODUCTS
              <FiChevronDown
                className={`text-xs transition-transform ${productsOpen ? "rotate-180" : ""
                  }`}
              />
            </button>

            {productsOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 mt-4 w-[750px] bg-neutral-900/95 border border-white/10 rounded-xl shadow-xl p-4 backdrop-blur">
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-5 border-r border-white/10 pr-3">
                    <h4 className="text-xs uppercase tracking-[0.25em] text-white/60 mb-2">
                      Categories
                    </h4>
                    <ul className="space-y-1">
                      {categories.map((cat) => (
                        <li key={cat.categoryId}>
                          <button
                            onMouseEnter={() => setActiveCat(cat.categoryId)}
                            onClick={() => goProducts(cat.categoryId)}
                            className={`w-full text-left px-2 py-2 rounded-md ${activeCat === cat.categoryId
                              ? "bg-white/10 text-white"
                              : "text-white/80 hover:bg-white/5"
                              }`}
                          >
                            <span className="inline-flex items-center gap-2">
                              <FiChevronRight
                                className={`transition ${activeCat === cat.categoryId
                                  ? "opacity-100"
                                  : "opacity-0"
                                  }`}
                              />
                              {cat.categoryName}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="col-span-7 pl-3">
                    <h4 className="text-xs uppercase tracking-[0.25em] text-white/60 mb-2">
                      Subcategories
                    </h4>
                    <div className="grid grid-cols-2 gap-2 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
                      {(products[activeCat] || []).map((p) => (
                        <button
                          key={p.productId}
                          onClick={() => goProducts(activeCat, p.slug)}
                          className="text-left px-3 py-2 rounded-md border border-white/10 bg-white/[0.05] text-white/90 hover:bg-white/10 transition text-xs font-bold truncate"
                        >
                          {p.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <NavLink
            to="/projects"
            className={({ isActive }) =>
              `relative font-medium transition-all duration-300 group ${isActive ? "text-white" : "text-white/80 hover:text-white"
              }`
            }
          >
            PROJECTS
            <span className="absolute left-0 bottom-0 w-0 h-[2px] bg-white transition-all duration-300 group-hover:w-full"></span>
          </NavLink>

          <NavLink
            to="/contact"
            className={({ isActive }) =>
              `relative font-medium transition-all duration-300 group ${isActive ? "text-white" : "text-white/80 hover:text-white"
              }`
            }
          >
            CONTACT
            <span className="absolute left-0 bottom-0 w-0 h-[2px] bg-white transition-all duration-300 group-hover:w-full"></span>
          </NavLink>

          {settings?.showBuyNow && (
            <a
              href={PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 px-6 py-2 rounded-full border border-white/20 text-black font-medium bg-white hover:text-black transition-all duration-300 scale-95 hover:scale-100"
            >
              BUY NOW
            </a>
          )}
        </nav>

        <button
          className="md:hidden text-white"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <FiX size={26} /> : <FiMenu size={26} />}
        </button>
      </div>

      {isOpen && (
        <div className="md:hidden bg-neutral-900 border-t border-white/10 max-h-[80vh] overflow-y-auto">
          <ul className="p-5 space-y-2">
            <li>
              <NavLink
                to="/"
                onClick={() => setIsOpen(false)}
                className="block py-2 text-white/90"
              >
                HOME
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/about"
                onClick={() => setIsOpen(false)}
                className="block py-2 text-white/90"
              >
                ABOUT
              </NavLink>
            </li>

            <li>
              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer py-2 text-white/90">
                  PRODUCTS
                  <FiChevronDown className="group-open:rotate-180 transition" />
                </summary>
                <div className="pl-4">
                  {categories.map((cat) => (
                    <div key={cat.categoryId} className="mb-2">
                      <button
                        onClick={() => goProducts(cat.categoryId)}
                        className="text-white font-medium text-sm"
                      >
                        {cat.categoryName}
                      </button>
                      <ul className="ml-2 mt-1 space-y-1">
                        {(products[cat.categoryId] || []).map((p) => (
                          <li key={p.productId}>
                            <button
                              onClick={() => goProducts(cat.categoryId, p.slug)}
                              className="block text-white/70 hover:text-white text-xs py-1"
                            >
                              — {p.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </details>
            </li>

            <li>
              <NavLink
                to="/projects"
                onClick={() => setIsOpen(false)}
                className="block py-2 text-white/90"
              >
                PROJECTS
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/contact"
                onClick={() => setIsOpen(false)}
                className="block py-2 text-white/90"
              >
                CONTACT
              </NavLink>
            </li>
            {settings?.showBuyNow && (
              <li className="pt-4 border-t border-white/5">
                <a
                  href={PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setIsOpen(false)}
                  className="block w-full py-4 text-center rounded-xl bg-white text-black font-bold uppercase tracking-widest text-xs hover:bg-white/90 transition shadow-lg"
                >
                  BUY NOW
                </a>
              </li>
            )}
          </ul>
        </div>
      )}
    </header>
  );
}
